/**
 * DEX Order Storage Utility
 *
 * Persists DEX order data to localStorage for recovery purposes.
 * Uses individual keys per transfer for fault isolation.
 *
 * Key pattern: parse_dex_${transferId}
 *
 * Status flow:
 * - 'pending': Order created, awaiting user signature
 * - 'signed': Transaction signed and broadcast
 * - 'confirmed': Source tx confirmed on-chain
 * - 'polling': Polling for order fulfillment (deBridge cross-chain)
 * - 'complete': Order fulfilled
 * - 'failed': Order failed or cancelled
 */

import { isReceiptUnknownMessage } from './errors'

const STORAGE_PREFIX = 'parse_dex_'

/**
 * #261 F9 — pre-migration `orderStatus` → the phase to record as `failedStep` when the stale-order
 * migration below rewrites a stuck order to 'failed'. Once that rewrite happens the original status
 * is gone, so this is the ONLY chance to say which phase the order died in; without it the progress
 * view can only show all three steps failed.
 *
 * Values are in `DEX_ORDER`'s vocabulary (utils/steps.ts) — the same phase names makeDexSteps gates
 * on — NOT this module's `DexOrderStatus` names.
 *
 * 'pending' maps to 'broadcasting', the same as 'signed'/'confirmed', BECAUSE OF THE BRANCH ABOVE:
 * the migration removes every 'pending' order that has no `sourceTxHash`, so a 'pending' order that
 * reaches the stale branch necessarily HAS one. 'fetching_quote' would contradict that precondition —
 * it reds the quote row while the sign row displays that broadcast transaction's explorer link
 * (steps.ts renders `sourceTxHash` there), i.e. it blames the step before the one whose tx is on
 * screen.
 */
const STALE_MIGRATION_FAILED_STEP: Record<string, string> = {
  polling: 'polling',
  signed: 'broadcasting',
  confirmed: 'broadcasting',
  pending: 'broadcasting',
}

// Migration: clear stale pre-v2 orders. Covers two cases:
// 1. Unsigned pending orders (persisted before user signed — old code path)
// 2. Non-terminal orders stuck for >1 hour (signed/confirmed/polling that
//    never reached 'complete' or 'failed' due to JS errors mid-flow)
try {
  const STALE_THRESHOLD = 60 * 60 * 1000 // 1 hour
  const _now = Date.now()
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i)
    if (key?.startsWith(STORAGE_PREFIX)) {
      const raw = localStorage.getItem(key)
      if (raw) {
        const o = JSON.parse(raw)
        const isTerminal = o.orderStatus === 'complete' || o.orderStatus === 'failed'
        if (o.orderStatus === 'pending' && !o.sourceTxHash) {
          localStorage.removeItem(key)
        } else if (
          !isTerminal &&
          _now - (o.updatedAt || o.createdAt) > STALE_THRESHOLD &&
          !isReceiptUnknownMessage(o.error)
        ) {
          // DEV-375: a receipt-unknown order (I10) is deliberately left non-terminal so the
          // background fill can still correct it — this migration runs at import time, before any
          // reconcile pass, so without this exemption a closed tab reopened >1h later would have its
          // hash-bearing "don't submit it twice" message silently replaced with a terminal false
          // 'Order timed out' on a transfer that may have succeeded. Every other stuck order is
          // unaffected — this only carves out the receipt-unknown message.
          //
          // Stale non-terminal order — mark as failed so it shows correctly.
          // #261 F9: capture the failing phase FIRST, from the status about to be overwritten.
          const staleFailedStep = STALE_MIGRATION_FAILED_STEP[o.orderStatus as string]
          if (staleFailedStep) o.failedStep = staleFailedStep
          o.orderStatus = 'failed'
          o.error = 'Order timed out'
          o.updatedAt = _now
          localStorage.setItem(key, JSON.stringify(o))
        }
      }
    }
  }
} catch { /* ignore */ }
const MAX_AGE_MS = 24 * 60 * 60 * 1000 // 24 hours for pending orders
const TERMINAL_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000 // 30 days for completed/failed

export type DexOrderStatus = 'pending' | 'signed' | 'confirmed' | 'polling' | 'complete' | 'failed'

export interface StoredDexOrder {
  transferId: string
  orderId?: string
  sourceChain: string
  destinationChain: string
  sourceAddress: string
  destinationAddress: string
  sourceToken: string
  destToken: string
  amount: string
  provider: string
  sourceTxHash?: string
  /** Destination/fill tx hash — captured from the order-status poll on fulfillment so the Activity row + the
      progress view can show a "Delivering to {chain}" explorer link. Not known until the order settles. */
  destTxHash?: string
  orderStatus: DexOrderStatus
  createdAt: number
  updatedAt: number
  error?: string
  /**
   * #261 F9 — the phase the order was in when it failed, in `DEX_ORDER`'s vocabulary
   * (utils/steps.ts: 'fetching_quote' | 'confirming_route' | 'approving' | 'signing' |
   * 'broadcasting' | 'polling'). Written by every path that sets `orderStatus: 'failed'` so the
   * progress view — live OR reopened from Activity after a reload — can red only the step that
   * actually failed. Optional: rows persisted before F9 simply lack it and fall back to the old
   * all-steps-failed rendering. Written REGARDLESS of the render flag, so a failure recorded while
   * VITE_FAILURE_STEP_STATE is off is still labelled if it is later turned on.
   */
  failedStep?: string
  /**
   * DEV-375 finding #6 — consecutive 404s `reconcileDexOrders` has seen for this transferId WHILE it
   * is receipt-unknown (I10). A Jupiter same-chain transferId 404s on `GET /api/dex/order/:id`
   * PERMANENTLY (it's inserted into `transfers`, not `near1click_orders`/`debridge_orders`), so
   * without a stop rule finding #3's 30-day retention turns a bounded ~5,700-request futile poll
   * into a ~173,000-request one. Reset to 0 on the next successful (non-404) status response — see
   * `reconcileDexOrders.ts`'s success branch — so it counts CONSECUTIVE 404s as documented, not
   * cumulative ones: the Jupiter same-chain 404 is permanent by construction, but this counter is
   * generic and must not abandon an order that answered healthily in between two transient gaps.
   */
  notFoundCount?: number
  /**
   * DEV-375 finding #6 — set once `notFoundCount` reaches the abandon threshold.
   * `selectReconcilableOrders` excludes an order with this set, which is what actually stops the
   * polling; the order itself stays non-terminal (`orderStatus` untouched) so it keeps its 30-day
   * retention, its hash, and its warning for display and for the user to check manually.
   */
  pollingAbandoned?: boolean
}

function getKey(transferId: string): string {
  return `${STORAGE_PREFIX}${transferId}`
}

export function saveDexOrder(order: StoredDexOrder): void {
  try {
    localStorage.setItem(getKey(order.transferId), JSON.stringify(order))
  } catch (e) {
    console.warn('Failed to save DEX order:', e)
  }
}

export function getDexOrder(transferId: string): StoredDexOrder | null {
  try {
    const data = localStorage.getItem(getKey(transferId))
    if (data) {
      return JSON.parse(data)
    }
  } catch (e) {
    console.warn('Failed to get DEX order:', transferId, e)
  }
  return null
}

export function updateDexOrder(
  transferId: string,
  updates: Partial<StoredDexOrder>,
): StoredDexOrder | null {
  try {
    const order = getDexOrder(transferId)
    if (order) {
      const updated = { ...order, ...updates, updatedAt: Date.now() }
      localStorage.setItem(getKey(transferId), JSON.stringify(updated))
      return updated
    }
  } catch (e) {
    console.warn('Failed to update DEX order:', transferId, e)
  }
  return null
}

export function getAllActiveDexOrders(): StoredDexOrder[] {
  const orders: StoredDexOrder[] = []
  const now = Date.now()

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(STORAGE_PREFIX)) {
        try {
          const data = localStorage.getItem(key)
          if (data) {
            const order: StoredDexOrder = JSON.parse(data)
            // Orders with 'pending' status and no sourceTxHash were saved before
            // the user signed — they never committed on-chain and should be pruned.
            if (order.orderStatus === 'pending' && !order.sourceTxHash) {
              localStorage.removeItem(key)
              continue
            }
            const isTerminal = order.orderStatus === 'complete' || order.orderStatus === 'failed'
            // DEV-375 finding #3 — a receipt-unknown order (I10) is the ONE class this ticket exists
            // to preserve, and it is non-terminal by design (the background fill can still correct
            // it). Pruning it at the 24h `MAX_AGE_MS` — while a genuinely FAILED order keeps 30 days —
            // was a retention REGRESSION: before this branch the same order was stored 'failed' and
            // kept 30 days under a false label. Give it the terminal-length retention instead, same as
            // the import-time migration above already does for the same reason.
            const maxAge = (isTerminal || isReceiptUnknownMessage(order.error)) ? TERMINAL_MAX_AGE_MS : MAX_AGE_MS
            if (now - order.createdAt < maxAge) {
              orders.push(order)
            } else {
              localStorage.removeItem(key)
            }
          }
        } catch (e) {
          console.warn('Failed to parse DEX order entry, removing:', key, e)
          localStorage.removeItem(key)
        }
      }
    }
  } catch (e) {
    console.warn('Failed to read DEX order storage:', e)
  }

  return orders.sort((a, b) => b.createdAt - a.createdAt)
}

export function removeDexOrder(transferId: string): void {
  try {
    localStorage.removeItem(getKey(transferId))
  } catch (e) {
    console.warn('Failed to remove DEX order:', transferId, e)
  }
}

export function cleanupStaleDexOrders(): number {
  const now = Date.now()
  let cleanedCount = 0

  try {
    const keysToRemove: string[] = []

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(STORAGE_PREFIX)) {
        try {
          const data = localStorage.getItem(key)
          if (data) {
            const order: StoredDexOrder = JSON.parse(data)
            // Prune unsigned orders (saved pre-sign, never committed)
            if (order.orderStatus === 'pending' && !order.sourceTxHash) {
              keysToRemove.push(key)
              continue
            }
            const isTerminal = order.orderStatus === 'complete' || order.orderStatus === 'failed'
            // DEV-375 finding #3 — same exemption as `getAllActiveDexOrders` above; see its comment.
            const maxAge = (isTerminal || isReceiptUnknownMessage(order.error)) ? TERMINAL_MAX_AGE_MS : MAX_AGE_MS
            if (now - order.createdAt >= maxAge) {
              keysToRemove.push(key)
            }
          }
        } catch {
          keysToRemove.push(key)
        }
      }
    }

    keysToRemove.forEach(key => {
      localStorage.removeItem(key)
      cleanedCount++
    })
  } catch (e) {
    console.warn('Failed to cleanup DEX orders:', e)
  }

  return cleanedCount
}
